import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import { sizeCategoryDeleteMutation, sizeCategoryIndexOptions, sizeCategoryIndexQueryKey } from '$api-client/@tanstack/react-query.gen'
import { SizeCategory } from '$api-client/types.gen'
import { AnimatedOutlet, Breadcrumb, Button, ConfirmationModal, ElementIntent, GenericTable, OptionsCell, Text, TextCell, useModalState, usePageTitle } from '$components'
import { BinIcon, EditIcon, PlusIcon, SettingsIcon } from '$components'
import { ToastHelper, ToastType } from '$components'
import { SettingsRoutePath } from '$features/settings/routes/protected'
import { Router } from '$utils/router'
import { TabLayout } from '$features/settings/layouts/tab'

const columnHelper = createColumnHelper<SizeCategory>()

export function SizesPage() {
  usePageTitle('Settings - Size Categories')

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { modalState, open, close } = useModalState()

  const { data: sizes } = useQuery({
    ...sizeCategoryIndexOptions(),
  })

  const { mutate: deleteSize, isPending: isDeleting } = useMutation({
    ...sizeCategoryDeleteMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sizeCategoryIndexQueryKey() })
      ToastHelper.deleteSuccess(ToastType.SizeCategory)
      close()
    },
    onError: () => ToastHelper.deleteError(ToastType.SizeCategory),
  })

  const columns = useMemo(() => [
    columnHelper.accessor(row => row.values?.name, {
      id: 'name',
      header: 'Name',
      cell: info => <TextCell>{info.getValue()}</TextCell>,
    }),
    columnHelper.display({
      id: 'weight_range',
      header: 'Weight Range',
      cell: ({ row }) => (
        <TextCell>
          {row.original.values?.from_weight_lbs} – {row.original.values?.to_weight_lbs} lbs
        </TextCell>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      maxSize: 66,
      minSize: 66,
      cell: ({ row }) => (
        <OptionsCell
          options={[
            {
              id: 'edit',
              value: 'Edit',
              icon: EditIcon,
              onClick: () => navigate(Router.buildRoute(SettingsRoutePath.Settings, SettingsRoutePath.SettingsPetOptions, Router.getRoute(SettingsRoutePath.SettingsSizeCategoryUpdate, { sizeCategoryId: row.original.id }))),
            },
            {
              id: 'delete',
              value: 'Delete',
              intent: ElementIntent.Danger,
              icon: BinIcon,
              onClick: () => open(row.original.id),
            },
          ]}
        />
      ),
    }),
  ], [navigate, open])

  return (
    <TabLayout>
      <Breadcrumb
        items={[
          {
            label: 'Settings',
            icon: SettingsIcon,
            path: SettingsRoutePath.Settings,
          },
          {
            label: 'Size Categories',
            path: Router.buildRoute(SettingsRoutePath.Settings, SettingsRoutePath.SettingsPetOptionsSizes),
          },
        ]}
      />

      <div className="flex items-center justify-between">
        <div>
          <Text
            type="h2"
            weight="medium"
          >
            Size Categories
          </Text>

          <Text
            type="s"
            className="text-fg-subtle mt-stack-dense"
          >
            Define weight-based size categories used for pricing and filtering.
          </Text>
        </div>

        <Button
          variant="primary"
          icon={PlusIcon}
          onClick={() => navigate(Router.buildRoute(SettingsRoutePath.Settings, SettingsRoutePath.SettingsPetOptions, 'sizes/create'))}
        >
          Add Size
        </Button>
      </div>

      {sizes?.data && sizes.data.length > 0
        ? (
          <div>
            <GenericTable.Container>
              <GenericTable
                data={sizes.data}
                columns={columns}
              />
            </GenericTable.Container>
          </div>
        )
        : (
          <Text
            type="s"
            className="text-fg-subtle"
          >
            No sizes added yet.
          </Text>
        )}

      {modalState.isOpen && (
        <ConfirmationModal
          isLoading={isDeleting}
          intent={ElementIntent.Danger}
          onClose={close}
          onConfirm={() => deleteSize({ path: { sizeCategory: modalState.modelId } })}
          title="Delete Size"
          description="Are you sure you want to delete this size? This may affect existing services and pets."
        />
      )}

      <AnimatedOutlet />
    </TabLayout>
  )
}
